import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Car, CalendarCheck, CalendarDays, Clock, MessageSquare, LogOut, BarChart3,
  Wrench, UserCheck, DollarSign, ShieldAlert, Tag, Users, Activity, Globe, Settings,
  SlidersHorizontal, ScrollText, Timer, ShieldCheck, BadgeCheck, Camera, Siren,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useAdminData } from "@/hooks/useAdminData";
import { CarModal, DiscountModal, TuroImportModal, AvailabilityModal } from "@/components/admin/AdminModals";
import { CarsPanel, DiscountsPanel } from "@/components/admin/AdminPanels";
import { InboxPanel } from "@/components/admin/InboxPanel";
import { FleetCalendar } from "@/components/admin/FleetCalendar";
import { FleetOverviewCalendar } from "@/components/admin/FleetOverviewCalendar";
import { PerformancePanel } from "@/components/admin/PerformancePanel";
import { MaintenanceManager } from "@/components/maintenance/MaintenanceManager";
import { ContactsCRM } from "@/components/admin/ContactsCRM";
import { TripsPanel, HostsPanel, PayoutsPanel, ClaimsPanel, ClaimModal, RejectModal } from "@/components/admin/AdminExtraPanels";
import { AdminSettings } from "@/components/admin/AdminSettings";
import { HostProgramPanel, AuditLogPanel } from "@/components/admin/OpsPanels";
import { OpsActivityPanel } from "@/components/admin/OpsActivityPanel";
import { IdentityPanel } from "@/components/admin/IdentityPanel";
import { InspectionsPanel } from "@/components/admin/InspectionsPanel";
import { HostVerificationsPanel } from "@/components/admin/HostVerificationsPanel";
import { FraudPanel } from "@/components/admin/FraudPanel";
import WebAnalytics from "@/components/admin/WebAnalytics";
import { Seo } from "@/components/Seo";

const NAV = [
  { group: "Overview", items: [["performance", "Dashboard", BarChart3], ["webanalytics", "Web Analytics", Activity]] },
  { group: "Trips", items: [["calendar", "Calendar", CalendarDays], ["trips", "Trips", CalendarCheck]] },
  { group: "Fleet", items: [["cars", "Vehicles", Car], ["maintenance", "Maintenance", Wrench]] },
  { group: "Marketplace", items: [["hosts", "Host Listings", UserCheck], ["payouts", "Payouts", DollarSign], ["claims", "Claims", ShieldAlert]] },
  { group: "Trust & Safety", items: [["identity", "Identity Checks", ShieldCheck], ["hostverify", "Host Verify", BadgeCheck], ["inspections", "Inspections", Camera], ["fraud", "Fraud Center", Siren]] },
  { group: "Engage", items: [["contacts", "CRM", Users], ["inbox", "Inbox", MessageSquare], ["discounts", "Discounts", Tag]] },
  { group: "Account", items: [["settings", "Users & Login", Settings], ["hostprogram", "Host Program", SlidersHorizontal], ["audit", "Activity Log", ScrollText]] },
];
// Team Activity is a super-admin-only oversight view of Operations Managers.
const TEAM_NAV = { group: "Team", items: [["opsactivity", "Team Activity", Timer]] };
const LABELS = Object.fromEntries(NAV.flatMap((g) => g.items.map(([k, l]) => [k, l])));
const EMPTY_CAR = { name: "", make: "Fiat", category: "Economy", weekly_rate: 350, deposit: 200, seats: 5, transmission: "Automatic", fuel: "Gasoline", image_url: "", images: "", description: "", features: [], available: true };

export default function AdminDashboard() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("performance");
  const [modal, setModal] = useState(null);
  const [availModal, setAvailModal] = useState(null);
  const [turoOpen, setTuroOpen] = useState(false);
  const [discountModal, setDiscountModal] = useState(null);
  const [claimModal, setClaimModal] = useState(null);
  const [rejectApp, setRejectApp] = useState(null);
  const [calView, setCalView] = useState("overview");

  const d = useAdminData();
  const { stats, bookings, cars, discounts, hostApplications, payouts, claims, hosts, load } = d;

  const isSuperAdmin = user?.role === "admin";
  const nav = isSuperAdmin ? [...NAV, TEAM_NAV] : NAV;
  const labels = { ...LABELS, opsactivity: "Team Activity" };

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const statCards = [
    { icon: Car, label: "Vehicles", value: stats.total_cars ?? "—" },
    { icon: CalendarCheck, label: "Bookings", value: stats.total_bookings ?? "—" },
    { icon: Clock, label: "Pending", value: stats.pending_bookings ?? "—" },
    { icon: UserCheck, label: "Host Reviews", value: stats.host_listings_pending ?? "—" },
    { icon: ShieldAlert, label: "Open Claims", value: stats.open_claims ?? "—" },
    { icon: MessageSquare, label: "Contacts", value: stats.total_contacts ?? "—" },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-slate-50 text-slate-900">
      <Seo noindex title="Admin Dashboard | Belo Rentals" path="/admin" />
      <header className="border-b border-slate-200 bg-white px-5 sm:px-8 h-16 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-2.5">
          <img src={`${process.env.PUBLIC_URL}/belo-wordmark.png`} alt="Belo Rentals" className="h-6 w-auto" />
          <span className="text-slate-400 text-xs font-semibold uppercase tracking-wide ml-1 border-l border-slate-200 pl-2.5">Admin</span>
        </div>
        <div className="flex items-center gap-5">
          <span className="text-slate-500 text-sm hidden sm:block">{user?.email}</span>
          <button onClick={() => navigate("/")} data-testid="admin-back-to-site" className="flex items-center gap-2 text-slate-600 hover:text-blue-600 font-medium transition-colors">
            <Globe size={16} /> <span className="hidden sm:inline">View site</span>
          </button>
          <button onClick={doLogout} data-testid="logout-btn" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium transition-colors">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar (md+) */}
        <aside className="hidden md:flex flex-col w-60 shrink-0 border-r border-slate-200 bg-white min-h-[calc(100vh-4rem)] sticky top-16 self-start py-6 px-3">
          {nav.map((g) => (
            <div key={g.group} className="mb-5">
              <p className="label-caps text-slate-400 px-3 mb-2 text-[10px]">{g.group}</p>
              <div className="space-y-1">
                {g.items.map(([k, label, Icon]) => {
                  const badge = k === "hosts" ? stats.host_listings_pending : k === "claims" ? stats.open_claims : 0;
                  return (
                    <button key={k} onClick={() => setTab(k)} data-testid={`nav-${k}`}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${tab === k ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                      <Icon size={17} /> <span className="flex-1 text-left">{label}</span>
                      {badge > 0 && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${tab === k ? "bg-white/25 text-white" : "bg-amber-100 text-amber-700"}`}>{badge}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </aside>

        <main className="flex-1 min-w-0 px-5 sm:px-8 py-6 max-w-7xl">
          {/* Mobile nav */}
          <div className="md:hidden flex gap-2 overflow-x-auto pb-3 mb-2 -mx-1 px-1">
            {nav.flatMap((g) => g.items).map(([k, label, Icon]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`mnav-${k}`}
                className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold ${tab === k ? "bg-blue-600 text-white" : "bg-white text-slate-600 shadow-card"}`}>
                <Icon size={15} /> {label}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between mb-5">
            <h1 className="font-heading text-2xl font-bold" data-testid="section-title">{labels[tab]}</h1>
          </div>

          {tab === "performance" && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-2">
                {statCards.map((s) => (
                  <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/ /g, "-")}`} className="bg-white rounded-2xl shadow-card p-5">
                    <span className="grid place-items-center w-9 h-9 rounded-xl bg-blue-50 text-blue-600"><s.icon size={16} /></span>
                    <p className="font-heading text-2xl mt-3 font-bold">{s.value}</p>
                    <p className="label-caps text-slate-400 mt-1 text-[10px]">{s.label}</p>
                  </div>
                ))}
              </div>
              <PerformancePanel />
            </>
          )}
          {tab === "webanalytics" && <WebAnalytics />}
          {tab === "calendar" && (
            <div>
              <div className="inline-flex items-center bg-slate-100 rounded-full p-1 mb-2">
                <button onClick={() => setCalView("overview")} data-testid="calview-overview" className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${calView === "overview" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>Fleet overview</button>
                <button onClick={() => setCalView("pervehicle")} data-testid="calview-pervehicle" className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${calView === "pervehicle" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>Per-vehicle & blocks</button>
              </div>
              {calView === "overview" ? <FleetOverviewCalendar apiBase="/admin" canManage /> : <FleetCalendar cars={cars} onMutate={load} />}
            </div>
          )}
          {tab === "trips" && <TripsPanel bookings={bookings} updateStatus={d.updateStatus} resendReceipt={d.resendReceipt} onClaim={(b) => setClaimModal({ booking_id: b.id })} onRecordPayment={d.markOfflinePaid} onMutate={load} />}
          {tab === "inbox" && <InboxPanel />}
          {tab === "maintenance" && <MaintenanceManager apiBase="/admin" cars={cars} />}
          {tab === "hosts" && (
            <HostsPanel applications={hostApplications} cars={cars} hosts={hosts}
              onApprove={(carId) => d.reviewCar(carId, "approved")}
              onReject={(app) => setRejectApp(app)} />
          )}
          {tab === "payouts" && <PayoutsPanel payouts={payouts} onMarkPaid={d.markPayoutPaid} onStripeTransfer={d.stripeTransfer} />}
          {tab === "identity" && <IdentityPanel />}
          {tab === "hostverify" && <HostVerificationsPanel />}
          {tab === "inspections" && <InspectionsPanel />}
          {tab === "fraud" && <FraudPanel />}
          {tab === "claims" && (
            <ClaimsPanel claims={claims} bookings={bookings} onNew={() => setClaimModal({})} onEdit={(c) => setClaimModal(c)}
              onUpdate={d.updateClaim} onDelete={d.deleteClaim} />
          )}
          {tab === "cars" && (
            <CarsPanel cars={cars}
              onAdd={() => setModal({ ...EMPTY_CAR })}
              onImport={() => setTuroOpen(true)}
              onEdit={(c) => setModal({ ...c, features: (c.features || []).join(", "), images: (c.images || []).join("\n") })}
              onManageDates={(c) => setAvailModal(c)}
              onToggle={d.toggleAvailability} onDelete={d.deleteCar} onReview={d.reviewCar} />
          )}
          {tab === "discounts" && (
            <DiscountsPanel discounts={discounts}
              onAdd={() => setDiscountModal({ code: "", type: "amount_25", active: true, max_uses: "" })}
              onEdit={(disc) => setDiscountModal({ ...disc, max_uses: disc.max_uses == null ? "" : disc.max_uses })}
              onToggle={d.toggleDiscount} onDelete={d.deleteDiscount} />
          )}
          {tab === "contacts" && <ContactsCRM cars={cars} onMutate={load} />}
          {tab === "settings" && <AdminSettings />}
          {tab === "hostprogram" && <HostProgramPanel />}
          {tab === "audit" && <AuditLogPanel />}
          {tab === "opsactivity" && isSuperAdmin && <OpsActivityPanel />}
        </main>
      </div>

      {modal && <CarModal data={modal} onClose={() => setModal(null)} onSave={(c) => d.saveCar(c, () => setModal(null))} />}
      {availModal && <AvailabilityModal car={availModal} onClose={() => setAvailModal(null)} onMutate={load} />}
      {turoOpen && <TuroImportModal onClose={() => setTuroOpen(false)} onMutate={load} />}
      {discountModal && <DiscountModal data={discountModal} onClose={() => setDiscountModal(null)} onSave={(c) => d.saveDiscount(c, () => setDiscountModal(null))} />}
      {claimModal && <ClaimModal data={claimModal} bookings={bookings} onClose={() => setClaimModal(null)} onSave={(c) => d.saveClaim(c, () => setClaimModal(null))} />}
      {rejectApp && <RejectModal app={rejectApp} onClose={() => setRejectApp(null)} onConfirm={(reason) => { d.reviewCar(rejectApp.car_id, "rejected", reason); setRejectApp(null); }} />}
    </div>
  );
}
